// src/config/biometricAuth.js
// Native-only biometric credential storage for MFSetu mobile apps.
// Never call this helper from the normal website/browser path.

import { Capacitor } from "@capacitor/core";
import {
  Accessibility,
  PqSecureStorage,
} from "capacitor-pq-secure-storage";

const MFD_KEY = "mfsetu_mfd_biometric_login_v1";
const CLIENT_KEY = "mfsetu_client_biometric_login_v1";

const appVariant = String(process.env.REACT_APP_APP_VARIANT || "")
  .trim()
  .toLowerCase();

const keyFor = (realm) => (realm === "client" ? CLIENT_KEY : MFD_KEY);
const hintKeyFor = (realm) => `mfsetu_${realm}_biometric_account_hint`;

export const isNativeBiometricRealm = (realm) => {
  if (!Capacitor.isNativePlatform()) return false;
  if (realm === "mfd") return appVariant === "mfd";
  if (realm === "client") return ["mywealth", "client"].includes(appVariant);
  return false;
};

export const getBiometricCapability = async (realm) => {
  if (!isNativeBiometricRealm(realm)) {
    return { available: false, reason: "NOT_NATIVE_APP" };
  }

  try {
    const caps = await PqSecureStorage.getHardwareCapabilities();
    return {
      available: caps?.biometricGated === true,
      hardwareBacked: caps?.hardwareBacked === true,
      reason: caps?.biometricGated === true ? "" : "BIOMETRIC_UNAVAILABLE",
    };
  } catch (error) {
    return { available: false, reason: error?.code || "BIOMETRIC_UNAVAILABLE" };
  }
};

export const hasBiometricLogin = async (realm) => {
  if (!isNativeBiometricRealm(realm)) return false;
  try {
    const { exists } = await PqSecureStorage.hasItem({ key: keyFor(realm) });
    return !!exists;
  } catch {
    return false;
  }
};

export const enableBiometricLogin = async (realm, credential) => {
  if (!isNativeBiometricRealm(realm)) throw new Error("Biometric login is mobile-app only.");
  if (!credential?.deviceCredential) throw new Error("Biometric device credential is required.");

  const key = keyFor(realm);
  const { exists } = await PqSecureStorage.hasItem({ key });
  if (exists) await PqSecureStorage.removeItem({ key });

  // Store only the dedicated opaque biometric device credential + minimal display metadata.
  // Normal session tokens and the user password are never persisted here.
  await PqSecureStorage.setItem({
    key,
    value: JSON.stringify({
      deviceCredential: String(credential.deviceCredential),
      userId: String(credential.userId || ""),
      displayName: String(credential.displayName || ""),
      isAdmin: !!credential.isAdmin,
      savedAt: Date.now(),
    }),
    requireBiometric: true,
    accessibility: Accessibility.WhenUnlockedThisDeviceOnly,
  });

  // Non-secret account hint: used only to detect account changes without
  // triggering a biometric read. It never contains a password or auth token.
  localStorage.setItem(hintKeyFor(realm), String(credential.userId || ""));
};

export const getBiometricAccountHint = (realm) =>
  isNativeBiometricRealm(realm) ? localStorage.getItem(hintKeyFor(realm)) || "" : "";

export const readBiometricLogin = async (realm) => {
  if (!isNativeBiometricRealm(realm)) return null;
  const { value } = await PqSecureStorage.getItem({ key: keyFor(realm) });
  if (!value) return null;

  try {
    const parsed = JSON.parse(value);
    if (!parsed?.deviceCredential) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const disableBiometricLogin = async (realm) => {
  if (!isNativeBiometricRealm(realm)) return;
  const key = keyFor(realm);
  const { exists } = await PqSecureStorage.hasItem({ key });
  if (exists) await PqSecureStorage.removeItem({ key });
  localStorage.removeItem(hintKeyFor(realm));
};

export const isBiometricCancellation = (error) =>
  ["E_AUTH_FAILED", "E_USER_CANCEL", "E_CANCELED", "E_CANCELLED"].includes(
    String(error?.code || "")
  );
